import { useAuthStore } from "@/features/authentication";
import Tooltip from "@components/ui/tooltip";
import { cn } from "@lib/cn";
import { SlidebarStoriesList } from "@pages/analytics";
import { useIsCreatedByMeView } from "@pages/stories/hooks/use-is-created-by-me-view";
import { useStoriesStoreShallow } from "@pages/stories/store";
import { IconChevronRight } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Folders } from "../components/folders";
import { PLATFORM_MENU } from "../constants";
import { useSidebarStoreShallow } from "../store";
import { ActiveNavigationLine } from "./active-navigation-line";
import { SidebarLibrariesMenu } from "./sidebar-libraries-menu";
import { SidebarStoriesMenu } from "./sidebar-stories-menu";

/**
 * Whether a folder under Stories is the active row, which is when the Stories
 * row steps back: a folder is its own list, named in the page header, so it
 * owns the highlight.
 *
 * Favorites and "Created by me" own the highlight the same way — each is drawn
 * as a filled row with the edge bar beside it — but that is decided in the
 * component, since one needs the signed-in user and both need the store.
 */
export function isFolderRowActive(
  activeAction: string | null,
  pathname: string,
): boolean {
  return (
    activeAction === "stories" && pathname.includes("/pages/editor/folder")
  );
}

type StoriesChild = "created-by-me" | "favorites";

/**
 * Where the edge bar sits for a row under Stories, measured from the top of
 * the Stories item: its own 32px row, then the submenu's 8px top padding, then
 * 32px rows with a 4px gap (SidebarStoriesMenu). `top` is transitioned, so the
 * bar slides between the two rows rather than jumping.
 */
const STORIES_CHILD_BAR_TOP: Record<StoriesChild, string> = {
  "created-by-me": "tw-top-10",
  favorites: "tw-top-[76px]",
};

export const SidebarBaseMenu = () => {
  "use no memo";
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [isStoriesOpen, setIsStoriesOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userType = useAuthStore((s) => s.userType);
  const { isOpen } = useSidebarStoreShallow((s) => ({
    isOpen: s.isOpen,
  }));
  const { setFolderId, isFavoritesView } = useStoriesStoreShallow((s) => ({
    setFolderId: s.setFolderId,
    isFavoritesView: s.isFavoritesView,
  }));

  const actionHandlers: Record<string, () => void> = useMemo(
    () => ({
      dashboard: () => navigate("/pages/home"),
      stories: () => {
        navigate("/pages/editor");
        setFolderId("");
      },
      integrations: () => navigate("/pages/profile/integrations"),
      analytics: () => navigate("/pages/analytics"),
      "power-ups": () => navigate("/pages/profile/add-ons"),
      automations: () => navigate("/pages/automations"),
    }),
    [navigate, setFolderId],
  );

  const handleClick = useCallback(
    (action: string) => {
      actionHandlers[action]?.();
    },
    [actionHandlers],
  );

  useEffect(() => {
    let action: string | null = null;
    if (pathname.includes("/pages/home")) {
      action = "dashboard";
    } else if (pathname.includes("/pages/editor/templates")) {
      action = null;
    } else if (pathname.includes("/pages/editor")) {
      action = "stories";
    } else if (pathname.includes("/pages/analytics")) {
      action = "analytics";
    } else if (pathname.includes("/pages/profile/integrations")) {
      action = "integrations";
    } else if (pathname.includes("/pages/profile/add-ons")) {
      action = "power-ups";
    } else if (pathname.includes("/pages/automations")) {
      action = "automations";
    }

    setActiveAction(action);
    setIsStoriesOpen(action === "stories");
  }, [pathname]);

  const isCreatedByMeView = useIsCreatedByMeView();
  /**
   * Which row under Stories owns the highlight, if any. The bar for it is
   * rendered here, on the Stories item's wrapper, because the submenu sits in
   * the collapsible's `overflow-hidden` and the bar hangs 16px outside the row.
   * With the sidebar collapsed the submenu is not rendered, and with the Stories
   * group folded the rows are out of sight, so no child can own anything and
   * Stories keeps the highlight itself.
   */
  const activeStoriesChild: StoriesChild | null =
    !isOpen || !isStoriesOpen
      ? null
      : isFavoritesView
        ? "favorites"
        : isCreatedByMeView
          ? "created-by-me"
          : null;
  const isNestedActive = useMemo(
    () =>
      isFolderRowActive(activeAction, pathname) ||
      (activeAction === "stories" && activeStoriesChild !== null),
    [activeAction, pathname, activeStoriesChild],
  );

  return (
    <div className="tw-flex tw-flex-col">
      <div
        data-tour="platform-nav"
        className={cn(
          "tw-flex tw-flex-col tw-px-4 tw-py-2 tw-transition-all tw-duration-300",
          !isOpen && "tw-px-2",
        )}
      >
        {isOpen && (
          <p className="tw-flex tw-h-8 tw-items-center tw-px-2 tw-font-sans tw-text-xs tw-font-normal tw-text-blue/70">
            Platform
          </p>
        )}
        <div className="tw-space-y-1">
          {PLATFORM_MENU.map((item) => {
            if (item.type && !item.type.includes(userType)) {
              return null;
            }
            const Icon = item.icon;
            const isActive = item.action === activeAction;
            const isStoriesOnNestedRoute =
              isNestedActive && item.action === "stories";
            const isVisuallyActive = isActive && !isStoriesOnNestedRoute;
            return (
              <div key={item.action} className="tw-relative tw-w-full">
                <Tooltip placement="right" offset={10}>
                  <Tooltip.Trigger asChild>
                    <button
                      onClick={() => handleClick(item.action)}
                      type="button"
                      className={cn(
                        "tw-flex tw-h-8 tw-w-full tw-items-center tw-justify-start tw-overflow-hidden tw-rounded-lg tw-p-2 tw-outline-none tw-transition-all tw-duration-300 hover:tw-bg-blue-100/50",
                        {
                          "tw-bg-blue-100 hover:tw-bg-blue-100":
                            isVisuallyActive,
                          "tw-gap-0": !isOpen,
                          "tw-bg-[transparent] hover:tw-bg-[transparent]":
                            isStoriesOnNestedRoute,
                        },
                      )}
                    >
                      <Icon
                        className={cn(
                          "tw-size-4 tw-shrink-0 tw-text-blue tw-transition-all tw-duration-300",
                          !isVisuallyActive && "tw-opacity-50",
                        )}
                      />
                      <span
                        className={cn(
                          "tw-text-sans tw-ml-2 tw-overflow-hidden tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-blue tw-transition-all tw-duration-300 tw-ease-in-out",
                          !isOpen &&
                            "tw-pointer-events-none tw-ml-0 !tw-w-0 tw-opacity-0",
                          !isVisuallyActive && "tw-opacity-50",
                        )}
                      >
                        {item.label}
                      </span>
                      {item.badge && isOpen && (
                        <span className="tw-ml-auto tw-rounded-full tw-bg-violet-100 tw-px-2 tw-py-0.5 tw-font-sans tw-text-xs tw-font-semibold tw-text-violet-700">
                          {item.badge}
                        </span>
                      )}
                      {item.action === "stories" && (
                        <span
                          role="button"
                          tabIndex={0}
                          className="tw-ml-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsStoriesOpen((prev) => !prev);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.stopPropagation();
                              setIsStoriesOpen((prev) => !prev);
                            }
                          }}
                        >
                          <IconChevronRight
                            className={cn(
                              "tw-size-4 tw-shrink-0 tw-text-blue tw-transition-all tw-duration-300 tw-ease-in-out",
                              {
                                "tw-rotate-90": isStoriesOpen,
                                "tw-opacity-50": !isVisuallyActive,
                                "tw-pointer-events-none tw-w-0 tw-opacity-0":
                                  !isOpen,
                              },
                            )}
                          />
                        </span>
                      )}
                    </button>
                  </Tooltip.Trigger>
                  <Tooltip.Content className={cn(isOpen && "tw-hidden")}>
                    <Tooltip.Text className="tw-rounded-md tw-bg-primary tw-px-3 tw-py-2 tw-text-sm tw-text-white">
                      {item.label}
                    </Tooltip.Text>
                  </Tooltip.Content>
                </Tooltip>
                {item.action === "stories" && isOpen && (
                  <div
                    className={cn(
                      "tw-grid tw-transition-[grid-template-rows] tw-duration-300",
                      {
                        "[grid-template-rows:1fr]": isStoriesOpen,
                        "[grid-template-rows:0fr]": !isStoriesOpen,
                      },
                    )}
                  >
                    <div className="tw-overflow-hidden">
                      <SidebarStoriesMenu />
                      <Folders />
                    </div>
                  </div>
                )}
                {item.action === "analytics" &&
                  isOpen &&
                  activeAction === "analytics" && (
                    <div className="tw-overflow-hidden">
                      <SlidebarStoriesList />
                    </div>
                  )}
                {isVisuallyActive && <ActiveNavigationLine isOpen={isOpen} />}
                {item.action === "stories" &&
                  activeAction === "stories" &&
                  activeStoriesChild !== null && (
                    <ActiveNavigationLine
                      isOpen={isOpen}
                      className={STORIES_CHILD_BAR_TOP[activeStoriesChild]}
                    />
                  )}
              </div>
            );
          })}
        </div>
      </div>
      <SidebarLibrariesMenu />
    </div>
  );
};
